import { PregnancyArticle } from "@aph/ui-lib-humanx";
import { format } from "date-fns/format";
import React, { useState } from "react";
import { useRouter } from "next/navigation";
import DeviceSelector from "@/components/ArticlePage/redux/selectors/userAgent";
import { ArticleAttributesInterface, ArticleDetailInterface } from "@/components/ArticlePage/api/interface";
import ArticleContent from "@/components/ArticlePage/Components/ArticleContent";
import ArticleGlance from "@/components/ArticlePage/Components/ArticleGlance";
import InThisArticle from "@/components/ArticlePage/Components/InThisArticle";
import RelatedArticles from "@/components/ArticlePage/Components/RelatedArticles";
import { COMPONENT_TYPES } from "@/components/ArticlePage/constants";
import { COMPONENT_TYPES as COMMON_COMPONENT_TYPES, FILTER_MAPPING, TYPE_ARTICLE } from "@/helpers/constants";
import ArticleSelector from "@/components/ArticlePage/redux/selectors/articleData";
import FAQ from "@/components/FAQ";
import ShoppingWidget3 from "@/components/ShoppingWidget3";
import { useAppSelector } from "@/redux/hooks";
import ShareIconModal from "@/components/ShareModal";
import ShareIcon from "$/icons/shareIcon";
import { DeviceInfo } from "@/interface/deviceInfo";
import IconPlus from "$/icons/icon-plus.svg";
import DoctorsWidget from "@/components/DoctorsWidget";
import { getResourceListingURL } from "@/helpers";
import Bookmark from "@/components/BookmarkAction";
import DiagnosticsWidget from "@/components/DiagnosticsWidget";
import ShoppingWidget2 from "@/components/ShoppingWidget2";

interface ComponentMappingProps {
  data: any;
  options?: any;
  isPharma?: boolean;
}

const ArticleComponentMapping = (props: ComponentMappingProps): React.ReactElement => {
  const { data, options = undefined } = props;
  const [openShareModal, setOpenShareModal] = useState<boolean>(false);
  const router = useRouter();
  const type = data?.ComponentType;
  const articleInfoObject: ArticleDetailInterface = useAppSelector(ArticleSelector);
  const deviceType: DeviceInfo = useAppSelector(DeviceSelector);
  const articleDataObject: ArticleAttributesInterface = articleInfoObject?.data?.[0]?.attributes;
  const isPharma = options?.isPharma || false;

  const handleAuthorClick = () => {
    articleDataObject?.AuthorUrl && router.push(articleDataObject?.AuthorUrl);
  };

  switch (type) {
    case COMPONENT_TYPES.BannerInfo:
      const categoryName = articleDataObject?.ArticleCategory?.data?.attributes?.CategoryName;

      return (
        <>
          <PregnancyArticle
            articleLabel={categoryName}
            title={articleDataObject?.Title}
            subTitle={articleDataObject?.Description}
            imageUrl={`${articleDataObject?.Image?.data?.attributes?.url}`}
            articleDate={format(new Date(articleDataObject?.PostedOn), "do MMM yyyy")}
            publisherName={articleDataObject?.AuthorName}
            articleReadTime={`${articleDataObject?.ReadTime} read`}
            isSocialMedia={true}
            postedDate={articleDataObject?.PostedOn}
            actionsData={[
              ...(isPharma
                ? []
                : [
                    {
                      component: (
                        <Bookmark
                          isFromListingPage={true}
                          type={TYPE_ARTICLE}
                          itemId={Number(articleInfoObject?.data?.[0]?.id)}
                          isPharma={isPharma}
                        />
                      ),
                    },
                  ]),
              {
                component: deviceType?.isMobile ? <span className="icon-ShareFat" /> : <ShareIcon />,
                onClick: () => setOpenShareModal(true),
              },
            ]}
            authorIcon={<IconPlus />}
            authorUrl={articleDataObject?.AuthorUrl}
            onAuthorClick={handleAuthorClick}
          />
          <ShareIconModal {...{ openPopup: openShareModal, setOpenPopup: setOpenShareModal }} />
        </>
      );

    case COMPONENT_TYPES.Glance:
      return <ArticleGlance title={data?.Heading} GlanceList={data?.GlanceList} />;

    case COMPONENT_TYPES.Index:
      return (
        <InThisArticle
          {...{
            hideListDropdown: options?.hideListDropdown,
            sectionRefs: options?.sectionRefs,
            setIsShowArticleFlyBottom: options?.setIsShowArticleFlyBottom,
            setArticleSectionTitle: options?.setArticleSectionTitle,
            isPharma,
          }}
        />
      );
    case COMPONENT_TYPES.RelatedArticles:
      const items = data?.relatedArticles && data?.relatedArticles?.length > 0 ? data?.relatedArticles : [];
      const filters = {};

      if (options?.clusterSlug) {
        filters[FILTER_MAPPING.cluster] = [options?.clusterSlug];
      } else if (options?.pillarSlug) {
        filters[FILTER_MAPPING.pillar] = [options?.pillarSlug];
      } else if (options?.categorySlug) {
        filters[FILTER_MAPPING.category] = [options?.categorySlug];
      }

      const ctaURL = getResourceListingURL(filters, "articles");

      return items?.length > 0 ? (
        <RelatedArticles
          items={items}
          title={data?.Title}
          ctaText={data?.CTAText}
          ctaURL={ctaURL}
          userAgent={deviceType}
          assetPosition={options?.index + 1}
          isPharma={isPharma}
        />
      ) : null;
    case COMPONENT_TYPES.ContentTitleDescription:
      return (
        <ArticleContent
          id={data?.id}
          title={data?.Heading}
          subTitle={data?.Description}
          setSectionRef={options?.setSectionRef}
          isPharma={isPharma}
          deviceType={deviceType}
        />
      );
    case COMMON_COMPONENT_TYPES.DoctorConsult:
      return (
        <DoctorsWidget
          data={data}
          userAgent={deviceType}
          currentSpeciality={options?.currentSpeciality}
          doctors={options?.doctorsArray}
          utm_medium="article-page"
        />
      );
    case COMMON_COMPONENT_TYPES.LabTest:
      return (
        <div className="spacingBetweenSections">
          <DiagnosticsWidget
            data={data}
            userAgent={deviceType}
            diagnostics={options?.diagnostics}
            utm_medium="article-page"
          />
        </div>
      );
    /*** This section is commented will handle in future  ***/
    // case COMPONENT_TYPES.ToolsWidget:
    //   const toolObject = data?.Tool?.data?.attributes;
    //   const imageUrl = data?.Tool?.data?.attributes?.Image?.data?.attributes?.url;

    //   return <AiGenerator {...{ title: toolObject?.Title, ctaText: "Generate Names", image: imageUrl }} />;
    case COMPONENT_TYPES.AudioWidget:
      // Will be handled in the future
      return <></>;
    case COMPONENT_TYPES.ContentDescription:
      return <ArticleContent id={data?.id} subTitle={data?.Description} setSectionRef={options?.setSectionRef} isPharma={isPharma} deviceType={deviceType} />;
    case COMPONENT_TYPES.FAQ:
      return <FAQ data={data} />;
    case COMPONENT_TYPES.ShopByProdId:
      return isPharma ? <ShoppingWidget2 data={data} desktopItems={2} userAgent={deviceType} products={options?.productInfo[data?.id]?.products} isPharma={isPharma} /> : <ShoppingWidget3 data={data} productInfo={options?.productInfo[data?.id]} isPharma={isPharma} />;
    default:
      return (
        <>
          <br />
        </>
      );
  }
};

export default ArticleComponentMapping;
